import { useSyncExternalStore, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { eventDebug, type RunDebugInfo } from '../../utils/event-debug';

/**
 * On-screen diagnostics overlay for the platform-event / run-stream pipeline.
 * Renders only when the debug flag is on (`localStorage['optr:debug:events']`
 * or `?evtdbg=1`; toggle live with `__eventDebug.on()` / `.off()`).
 *
 * Reads the global `eventDebug` store (NOT React context) so it can be mounted
 * once at the app root and still observe runs happening inside any
 * PlatformEventProvider subtree. See utils/event-debug.ts for the signals.
 */

const READY_STATE_LABEL: Record<number, string> = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
};

function RunRow({ run }: { run: RunDebugInfo }) {
  const total = Object.values(run.counts).reduce((a, b) => a + b, 0);
  const stepEvents = total; // every recorded event reached the runId subscriber
  // The smoking gun: a run that ended via poll having seen ~no WS step events.
  const suspicious = run.terminalVia === 'poll' || (total === 0 && !!run.terminalKind);
  const elapsed =
    run.firstEventAt && run.lastEventAt ? run.lastEventAt - run.firstEventAt : null;

  return (
    <div
      className={`border-t border-white/10 py-1.5 ${suspicious ? 'text-amber-300' : 'text-zinc-200'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-semibold" title={run.runId}>
          {run.runId.slice(0, 16)}…
        </span>
        <span className="shrink-0 tabular-nums">{stepEvents} ev</span>
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-zinc-400">
        <span>start ws:{run.connectedAtStart ? '✓' : '✗'}</span>
        {run.pollStarted && <span className="text-amber-400">poll-fallback</span>}
        {run.terminalKind && (
          <span>
            end:{run.terminalKind.replace('agent.run.', '')}/
            <span className={run.terminalVia === 'poll' ? 'text-amber-400' : 'text-emerald-400'}>
              {run.terminalVia}
            </span>
          </span>
        )}
        {elapsed != null && <span>{(elapsed / 1000).toFixed(1)}s</span>}
      </div>
      {total > 0 && (
        <div className="mt-0.5 text-[10px] text-zinc-500">
          {Object.entries(run.counts)
            .map(([t, c]) => `${t.replace('agent.run.', '')}×${c}`)
            .join('  ')}
        </div>
      )}
    </div>
  );
}

export function EventDebugHud() {
  useSyncExternalStore(eventDebug.subscribe, eventDebug.getVersion);
  const state = eventDebug.getSnapshotState();
  const [collapsed, setCollapsed] = useState(false);

  // Secret toggle: Cmd/Ctrl + Shift + Alt + D. No `description` so it stays
  // out of the Shift+? cheatsheet (internal dev binding — see
  // context/keyboard-shortcuts.md). Registered unconditionally so it can turn
  // the overlay ON even while it's hidden.
  useHotkeys(
    'mod+shift+alt+d',
    () => eventDebug.setEnabled(!eventDebug.isEnabled()),
    { preventDefault: true, enableOnFormTags: true },
    [],
  );

  if (!state.enabled) return null;

  const readyLabel =
    state.wsReadyState != null ? READY_STATE_LABEL[state.wsReadyState] ?? String(state.wsReadyState) : '—';

  return (
    <div
      className="fixed bottom-2 right-2 z-[99999] w-80 max-h-[70vh] overflow-hidden rounded-md border border-white/15 bg-zinc-900/95 font-mono text-[11px] text-zinc-200 shadow-xl backdrop-blur"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-white/10 bg-zinc-800/80 px-2 py-1">
        <div className="flex items-center gap-1.5">
          <span
            className={`inline-block h-2 w-2 rounded-full ${state.wsConnected ? 'bg-emerald-400' : 'bg-red-400'}`}
          />
          <span className="font-semibold">evt-dbg</span>
          <span className="text-zinc-400">{readyLabel}</span>
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <button className="hover:text-white" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? '▢' : '—'}
          </button>
          <button className="hover:text-white" onClick={() => eventDebug.setEnabled(false)}>
            ✕
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="max-h-[calc(70vh-2rem)] overflow-y-auto px-2 py-1.5">
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-zinc-400">
            <span>inbound</span>
            <span className="text-right tabular-nums text-zinc-200">{state.inboundTotal}</span>
            <span>unmatched</span>
            <span
              className={`text-right tabular-nums ${state.unmatchedTotal > 0 ? 'text-amber-400' : 'text-zinc-200'}`}
            >
              {state.unmatchedTotal}
            </span>
            <span>filters sent</span>
            <span className="text-right tabular-nums text-zinc-200">{state.lastFilters.length}</span>
            <span>active run</span>
            <span className="truncate text-right text-zinc-200" title={state.activeRunId}>
              {state.activeRunId ? state.activeRunId.slice(0, 12) + '…' : '—'}
            </span>
          </div>

          {state.lastFilters.length > 0 && (
            <details className="mt-1 text-[10px] text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300">filter union</summary>
              <pre className="mt-0.5 max-h-24 overflow-auto whitespace-pre-wrap break-all text-zinc-400">
                {JSON.stringify(state.lastFilters, null, 1)}
              </pre>
            </details>
          )}

          <div className="mt-1">
            {state.runs.length === 0 ? (
              <div className="py-2 text-center text-[10px] text-zinc-500">no runs yet</div>
            ) : (
              state.runs.map((run) => <RunRow key={run.runId} run={run} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
